import React, { useState, useCallback, useEffect } from 'react';
import { Alert, Button, Input, Modal, Radio, Select, Spin, Typography } from 'antd';
import { EyeInvisibleOutlined, EyeTwoTone, ReloadOutlined, WarningOutlined } from '@ant-design/icons';
import api, { getAllInfraUsers } from '../services/api';
import { useAuthGuard } from '../hooks/useAuthGuard';
import { useSidebar } from '../contexts/SidebarContext';
import { useSystemSettings } from '../hooks/useSystemSettings';
import { InfraUser } from '../types';
import { useNewGui } from '../hooks/useNewGui';
import PageHeader from './shared/PageHeader';
import { useRolePreview } from '../contexts/RolePreviewContext';

const { Text, Title } = Typography;

type PasswordType = 'login' | 'vnc';
const MIN_PW_LENGTH = 8;

function passwordStrength(pw: string): { label: string; color: string } {
    if (pw.length < MIN_PW_LENGTH) return { label: 'Too short', color: '#ff4d4f' };
    let score = 0;
    if (/[A-Z]/.test(pw)) score++;
    if (/[a-z]/.test(pw)) score++;
    if (/[0-9]/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;
    if (pw.length >= 16) score++;
    if (score <= 2) return { label: 'Weak', color: '#ff7a45' };
    if (score === 3) return { label: 'Fair', color: '#ffa940' };
    if (score === 4) return { label: 'Strong', color: '#73d13d' };
    return { label: 'Very Strong', color: '#389e0d' };
}

export default function PasswordResetPanel() {
    const { user } = useAuthGuard();
    const { isSidebarOpen } = useSidebar();
    const useNew = useNewGui();
    const { settings, loading: settingsLoading } = useSystemSettings();
    const { effectiveRole } = useRolePreview();
    const allowedRoles = ['itadmin', 'superadmin'];
    const role = effectiveRole || user?.role;
    const hasAccess = Boolean(user && role && allowedRoles.includes(role));
    const isEnabled = settings.PASSWORD_RESET_ENABLED !== 'false'; // default to visible while loading

    // --- data loading ---
    const [users, setUsers] = useState<InfraUser[]>([]);
    const [loadingUsers, setLoadingUsers] = useState(true);

    useEffect(() => {
        if (!hasAccess) return;
        getAllInfraUsers()
            .then((data: InfraUser[]) => setUsers(data ?? []))
            .catch(() => setUsers([]))
            .finally(() => setLoadingUsers(false));
    }, [hasAccess]);

    // Distinct user IDs and server infos derived from loaded users
    const userOptions = Array.from(new Set(users.map(u => u.UserId).filter(Boolean)));
    const serverInfoList = Array.from(new Set(users.map(u => u.ServerInfo).filter(Boolean)));

    // --- form state ---
    const [selectedUserId, setSelectedUserId] = useState('');
    const [selectedServerInfo, setSelectedServerInfo] = useState('');
    const [passwordType, setPasswordType] = useState<PasswordType>('login');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loadingSuggest, setLoadingSuggest] = useState(false);
    const [confirmVisible, setConfirmVisible] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

    const passwordsMatch = newPassword === confirmPassword && newPassword.length >= MIN_PW_LENGTH;
    const strength = passwordStrength(newPassword);
    const canSubmit = !!selectedUserId && !!selectedServerInfo && passwordsMatch && !submitting;

    // When user changes, pre-select their first server
    const handleUserChange = useCallback((uid: string) => {
        setSelectedUserId(uid);
        const firstServer = users.find(u => u.UserId === uid)?.ServerInfo ?? '';
        setSelectedServerInfo(firstServer);
    }, [users]);

    // Servers for the selected user only (fallback to all if nothing selected)
    const serversForUser = selectedUserId
        ? Array.from(new Set(users.filter(u => u.UserId === selectedUserId).map(u => u.ServerInfo).filter(Boolean)))
        : serverInfoList;

    const handleSuggest = useCallback(async () => {
        setLoadingSuggest(true);
        try {
            const { data } = await api.get<{ password: string }>('/infrauser/generate-password');
            setNewPassword(data.password);
            setConfirmPassword(data.password);
        } catch {
            // silent — user can type manually
        } finally {
            setLoadingSuggest(false);
        }
    }, []);

    const handleSubmit = useCallback(async () => {
        setConfirmVisible(false);
        setSubmitting(true);
        setResult(null);
        try {
            const { data } = await api.post<{ message: string }>('/infrauser/reset-password', {
                userId: selectedUserId,
                serverInfo: selectedServerInfo,
                passwordType,
                newPassword,
            });
            setResult({ type: 'success', message: data.message });
            setNewPassword('');
            setConfirmPassword('');
        } catch (error: any) {
            const msg = error?.response?.data?.message || error?.message || 'Password reset failed';
            setResult({ type: 'error', message: msg });
        } finally {
            setSubmitting(false);
        }
    }, [selectedUserId, selectedServerInfo, passwordType, newPassword]);

    if (!hasAccess) {
        return (
            <div className={`flex-1 p-6 transition-all duration-300 ${isSidebarOpen ? 'ml-64' : 'ml-20'}`}>
                <Alert type="error" message="Access denied. IT Admin or Superadmin role required." showIcon />
            </div>
        );
    }

    if (!settingsLoading && !isEnabled) {
        return (
            <div className={`flex-1 p-6 bg-gray-50 transition-all duration-300 ${isSidebarOpen ? 'ml-64' : 'ml-20'}`}>
                {useNew ? (
                <PageHeader title="Password Reset" purpose="Reset a login or VNC password for an infra user account. Recorded in audit." />
            ) : (
                <h1 className="text-3xl font-semibold text-gray-800 mb-6">Password Reset</h1>
            )}
                <Alert
                    type="warning"
                    showIcon
                    message="Password Reset is currently disabled"
                    description="A superadmin can enable it from Configuration."
                />
            </div>
        );
    }

    return (
        <div className={`flex-1 p-6 bg-gray-50 transition-all duration-300 ${isSidebarOpen ? 'ml-64' : 'ml-20'}`}>
            {useNew ? (
                <PageHeader title="Password Reset" purpose="Reset a login or VNC password for an infra user account. Recorded in audit." />
            ) : (
                <h1 className="text-3xl font-semibold text-gray-800 mb-6">Password Reset</h1>
            )}

            <div className="bg-white rounded-lg shadow-sm p-6" style={{ maxWidth: 560 }}>
            {loadingUsers ? (
                <Spin tip="Loading users..." />
            ) : (
                <>
                    {/* Step 1 — Select User */}
                    <div style={{ marginBottom: 16 }}>
                        <Text strong>User ID</Text>
                        <Select
                            showSearch
                            style={{ display: 'block', marginTop: 6 }}
                            placeholder="Search or select a user ID"
                            value={selectedUserId || undefined}
                            onChange={handleUserChange}
                            filterOption={(input, option) =>
                                (option?.value as string).toLowerCase().includes(input.toLowerCase())
                            }
                            options={userOptions.map(u => ({ value: u, label: u }))}
                        />
                    </div>

                    {/* Step 2 — Select Server (filtered to selected user's servers) */}
                    <div style={{ marginBottom: 16 }}>
                        <Text strong>Target Server</Text>
                        <Select
                            style={{ display: 'block', marginTop: 6 }}
                            placeholder="Select server"
                            value={selectedServerInfo || undefined}
                            onChange={setSelectedServerInfo}
                            options={serversForUser.map(s => ({ value: s, label: s }))}
                        />
                    </div>

                    {/* Step 3 — Password type */}
                    <div style={{ marginBottom: 16 }}>
                        <Text strong>Password Type</Text>
                        <div style={{ marginTop: 8 }}>
                            <Radio.Group value={passwordType} onChange={e => setPasswordType(e.target.value)}>
                                <Radio value="login">Login Password</Radio>
                                <Radio value="vnc">VNC Password</Radio>
                            </Radio.Group>
                        </div>
                    </div>

                    {/* Step 4 — New password */}
                    <div style={{ marginBottom: 8 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Text strong>New Password</Text>
                            <Button
                                size="small"
                                icon={<ReloadOutlined />}
                                loading={loadingSuggest}
                                onClick={handleSuggest}
                            >
                                Use Suggested
                            </Button>
                        </div>
                        <Input.Password
                            style={{ marginTop: 6 }}
                            value={newPassword}
                            onChange={e => setNewPassword(e.target.value)}
                            iconRender={visible => visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />}
                            placeholder={`Minimum ${MIN_PW_LENGTH} characters`}
                        />
                        {newPassword.length > 0 && (
                            <Text style={{ fontSize: 12, color: strength.color }}>{strength.label}</Text>
                        )}
                    </div>

                    <div style={{ marginBottom: 20 }}>
                        <Text strong>Confirm Password</Text>
                        <Input.Password
                            style={{ marginTop: 6 }}
                            value={confirmPassword}
                            onChange={e => setConfirmPassword(e.target.value)}
                            iconRender={visible => visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />}
                            placeholder="Re-enter new password"
                        />
                        {confirmPassword.length > 0 && newPassword !== confirmPassword && (
                            <Text type="danger" style={{ fontSize: 12 }}>Passwords do not match</Text>
                        )}
                    </div>

                    {result && (
                        <Alert
                            type={result.type}
                            message={result.message}
                            showIcon
                            closable
                            onClose={() => setResult(null)}
                            style={{ marginBottom: 16 }}
                        />
                    )}

                    <Button
                        type="primary"
                        danger
                        disabled={!canSubmit}
                        loading={submitting}
                        onClick={() => setConfirmVisible(true)}
                        block
                    >
                        Reset {passwordType === 'login' ? 'Login' : 'VNC'} Password
                    </Button>

                    {/* Confirmation modal */}
                    <Modal
                        open={confirmVisible}
                        title={
                            <span>
                                <WarningOutlined style={{ color: '#faad14', marginRight: 8 }} />
                                Confirm Password Reset
                            </span>
                        }
                        okText="Reset Now"
                        okButtonProps={{ danger: true, loading: submitting }}
                        cancelText="Cancel"
                        onOk={handleSubmit}
                        onCancel={() => setConfirmVisible(false)}
                    >
                        <Alert
                            type="warning"
                            showIcon
                            style={{ marginBottom: 16 }}
                            message="This will immediately change the user's password on the server."
                        />
                        <p><Text strong>User:</Text> {selectedUserId}</p>
                        <p><Text strong>Server:</Text> {selectedServerInfo}</p>
                        <p><Text strong>Type:</Text> {passwordType === 'login' ? 'Login (SSH/system)' : 'VNC'}</p>
                        <p style={{ marginTop: 12, color: '#888', fontSize: 12 }}>
                            Users with sudo/admin privileges are automatically blocked by the server-side check.
                        </p>
                    </Modal>
                </>
            )}
            </div>
        </div>
    );
}
